import { Injectable, signal } from '@angular/core';
import { AdminUser, LoginResponse } from './models';

const TOKEN_KEY = 'panakyc.admin.token';
const USER_KEY = 'panakyc.admin.user';

type JwtPayload = {
  exp?: number;
};

function getStorage(): Storage | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  return localStorage;
}

function decodeBase64Url(value: string): string | null {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return atob(padded);
  } catch {
    return null;
  }
}

function parseJwtPayload(token: string): JwtPayload | null {
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }

  const payloadText = decodeBase64Url(segments[1]);
  if (!payloadText) {
    return null;
  }

  try {
    const payload = JSON.parse(payloadText) as unknown;
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    return payload as JwtPayload;
  } catch {
    return null;
  }
}

function isJwtTokenValid(token: string | null, now = Date.now()): boolean {
  if (!token) {
    return false;
  }

  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
    return false;
  }

  return payload.exp * 1000 > now;
}

function readStoredUser(): AdminUser | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const rawUser = storage.getItem(USER_KEY);
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as AdminUser;
  } catch {
    return null;
  }
}

function readStoredToken(): string | null {
  return getStorage()?.getItem(TOKEN_KEY) ?? null;
}

@Injectable({ providedIn: 'root' })
export class AuthStoreService {
  private readonly tokenState = signal<string | null>(readStoredToken());
  private readonly userState = signal<AdminUser | null>(readStoredUser());

  private clearPersistedSession() {
    this.tokenState.set(null);
    this.userState.set(null);

    const storage = getStorage();
    storage?.removeItem(TOKEN_KEY);
    storage?.removeItem(USER_KEY);
  }

  private validatedToken(): string | null {
    const token = this.tokenState();
    if (!isJwtTokenValid(token)) {
      if (token !== null || this.userState() !== null) {
        this.clearPersistedSession();
      }

      return null;
    }

    return token;
  }

  readonly token = () => this.validatedToken();
  readonly currentUser = () => {
    this.validatedToken();
    return this.userState();
  };
  readonly isAuthenticated = () => this.validatedToken() !== null;
  readonly isPlatformUser = () => {
    this.validatedToken();
    const role = this.userState()?.role;
    return role === 'platform_administrator' || role === 'admin';
  };

  setSession(session: LoginResponse) {
    if (!isJwtTokenValid(session.token)) {
      this.clearPersistedSession();
      return;
    }

    this.tokenState.set(session.token);
    this.userState.set(session.user);

    const storage = getStorage();
    storage?.setItem(TOKEN_KEY, session.token);
    storage?.setItem(USER_KEY, JSON.stringify(session.user));
  }

  clearSession() {
    this.clearPersistedSession();
  }
}
